import { create } from 'zustand';
import { ApiRequest } from '@/utils/axios';
import { ISiteItem, ISteamItem } from '@/utils/types/Items';

export interface inventoryState {
    inventoryItems: ISiteItem[];
    steamItems: ISteamItem[];
    selectedItemIds: Set<number | bigint>;
    maxSelectedItems: number;
    selectedItemsPriceUsd: () => string;
    inventoryItemsPriceUsd: () => string;
    clearInventory: () => void;
    loadSiteInventory: () => Promise<void>;
    loadSiteInventoryDeposit: () => Promise<void>;
    setSelectedById: (id: number | bigint) => void;
    loadSteamInventory: (appId: number, contextId: number) => Promise<void>;
    uncacheSteamInventory: (appId: number, contextId: number) => Promise<void>;
    inventorySteamItemsPriceUsd: () => string;
    selectedSteamItemsPriceUsd: () => string;
}

export const inventoryStore = create<inventoryState>((set, get) => ({
    inventoryItems: new Array<ISiteItem>(),
    selectedItemIds: new Set<number>(),
    steamItems: new Array<ISteamItem>(),
    maxSelectedItems: 1,
    selectedItemsPriceUsd(): string {
        return Number(
            Array.from(get().selectedItemIds).reduce(
                (sum, itemId) => {
                    const item = get().inventoryItems.find((y) => y.id === itemId)?.item;
                    if (item) {
                        return Number(sum) + item.priceUsd;
                    } else {
                        return sum;
                    }
                },
                0 // Initialize the accumulator with 0
            )
        ).toFixed(2); // Convert the final result to a string with two decimal places
    },
    inventoryItemsPriceUsd(): string {
        return get()
            .inventoryItems.reduce((sum, inventory) => inventory.item.priceUsd + sum, 0)
            .toFixed(2);
    },
    inventorySteamItemsPriceUsd(): string {
        return get()
            .steamItems.reduce((sum, inventory) => inventory.priceUsd + sum, 0)
            .toFixed(2);
    },
    selectedSteamItemsPriceUsd(): string {
        return Number(
            Array.from(get().selectedItemIds).reduce(
                (sum, itemId) => {
                    const item = get().steamItems.find((y) => BigInt(y.assetId) === itemId);
                    if (item) {
                        return Number(sum) + item.priceUsd;
                    } else {
                        return sum;
                    }
                },
                0 // Initialize the accumulator with 0
            )
        ).toFixed(2); // Convert the final result to a string with two decimal places
    },
    async loadSiteInventory() {
        const {
            data: {
                response: { items },
            },
        } = await ApiRequest.get('/user/inventory/site');

        const {
            data: {
                response: {
                    deposit: { maxCount },
                },
            },
        } = await ApiRequest.get('/settings/list');

        set(() => ({ maxSelectedItems: maxCount, inventoryItems: items }));
    },
    async loadSiteInventoryDeposit() {
        // Wipe whatever is currently in the shared store before fetching. The
        // Withdraw page loads the FULL inventory (PRIZE winnings included) into
        // this same global list, so without this reset the deposit picker would
        // render those leftover prize skins on its first frame. They're not
        // bettable (the backend rejects them) but they must never be shown or
        // selectable here. clearInventory now clears items too for the same reason.
        set(() => ({ inventoryItems: [] }));
        await get().loadSiteInventory();
        set(() => ({
            inventoryItems: get().inventoryItems.filter((x) => !x.type.includes('PRIZE')),
        }));
    },
    async loadSteamInventory(appId: number, contextId: number) {
        get().clearInventory();
        const res: {
            data: { success: boolean; response: { items: ISteamItem[] } };
        } = await ApiRequest.post('/user/inventory/steam', {
            appId,
            contextId,
        });

        const {
            data: {
                response: {
                    deposit: { maxCount },
                },
            },
        } = await ApiRequest.get('/settings/list');

        if (res?.data?.success) {
            set(() => ({
                maxSelectedItems: maxCount,
                steamItems: res.data.response.items.filter((item) => item.priceUsd > 0.5),
            }));
        }
    },
    async uncacheSteamInventory(appId: number, contextId: number) {
        get().clearInventory();

        try {
            const {
                data: { response },
            } = await ApiRequest.get('/user/inventory/steam/uncache');
        } catch (err) {
            console.log(err);
        }
    },

    setSelectedById(id: number | bigint) {
        set((state) => {
            const newSelectedIds = new Set(state.selectedItemIds);

            if (newSelectedIds.has(id)) {
                newSelectedIds.delete(id);
            } else {
                if (newSelectedIds.size >= state.maxSelectedItems) return state;
                newSelectedIds.add(id);
            }

            return { selectedItemIds: newSelectedIds };
        });
    },
    clearInventory() {
        // Clear the loaded lists too, not just the selection. This store is a
        // single global shared by every inventory view (Withdraw, jackpot/coinflip
        // deposit, steam). The Withdraw page loads PRIZE winnings into it; if we
        // leave inventoryItems populated on unmount, the next deposit picker shows
        // those stale prize skins before its own filtered fetch resolves.
        set(() => ({
            selectedItemIds: new Set<number>(),
            inventoryItems: new Array<ISiteItem>(),
            steamItems: new Array<ISteamItem>(),
        }));
    },
}));
